import type { BuilderProduct } from "api-types/builders/builderTypes";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import type { StudioDocUserRole } from "api-types/studioDocs/studioDocTypes";
import type { UserAdminRole } from "api-types/users/userAdminRole";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { PolicyJson } from "common/cedar/cedarTypes";
import type { ClientBuilderUserHydrated } from "../builderUsers/builderUsersResponses";
import type { ClientCreditHydrated } from "../credits/creditsResponses";
import type { ClientBuilderRole } from "../permissions/permissionsResponses";
import type { ClientStudioDocMetadata } from "../studioDocs/studioDocMetadataTypes";
import type { ClientBuilder } from "./clientBuilderTypes";

export const clientBuilderProductDisplayNames: { [key in BuilderProduct]: string } = {
  api: "API",
  procure: "Procure",
  showroom: "Showroom",
  studio: "Studio",
  autoRenders: "AutoRender",
} as const;

export type ClientBuilderWithCredits = {
  builder: ClientBuilder;
  credits: ClientCreditHydrated[];
};

export type ClientBuilderAdminDetails = {
  builder: ClientBuilder;
  masterPlans: ClientMasterPlan[];
  builderUsers: ClientBuilderUserHydrated[];
  credits: ClientCreditHydrated[];
  /**
   * Whether the legacy home plan credit ledger is enabled for this builder. Resolved server side
   * because the admin dashboard has no builder route param, so the client cannot resolve a
   * builder scoped flag for the builder being viewed.
   */
  creditsEnabled: boolean;
  metrics: ClientBuilderPlanMetrics[];
};

export type ClientBuilderPlanMetrics = {
  builderId: string;
  planId: string;
  planType: "studio_docs" | "master_plans" | "community_plans";
  planName: string;
  homePlansCreated: number;
  homePlansBuilt: number;
};

export type ClientStudioDocMetadataForBuilder = {
  studioDoc: ClientStudioDocMetadata;
  users: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    adminRole: UserAdminRole;
    userRole?: StudioDocUserRole;
  }[];
};

export type ClientBuilderPoliciesAndRoles = {
  policies: PolicyJson[];
  roles: ClientBuilderRole[];
};

export type ClientBuilderPlanGltfTimingSpan = {
  startTimeMs?: number;
  durationMs: number;
};

export type ClientBuilderPlanGltfTiming = {
  buildingGeneration: ClientBuilderPlanGltfTimingSpan;
  geometryCollection: ClientBuilderPlanGltfTimingSpan;
  materialParamsResolution?: ClientBuilderPlanGltfTimingSpan;
  gltfSerialization: ClientBuilderPlanGltfTimingSpan;
  sourcePipelineTotal: ClientBuilderPlanGltfTimingSpan;
  sourceResponseTotal?: ClientBuilderPlanGltfTimingSpan;
};

export type ClientBuilderPlanGltfResponse = {
  /** 24-hour signed URL for a GLB in the private content bucket. */
  gltfUrl: string;
  /** 24-hour signed URL for the matching precomputed floor-plan sidecar. */
  floorPlanUrl: string;
  planData: ClientHomePlanBaseData;
  /** Choices supplied by the URL or caller, before defaults and option rules are resolved. */
  requestedOptionChoices: OptionChoices;
  selectedReportingUnitId?: string;
  timing: ClientBuilderPlanGltfTiming;
};

export type ClientBuilderPlanGltfOptionChangeResponse = {
  /** 24-hour signed URL for a GLB in the private content bucket. */
  gltfUrl: string;
  /** 24-hour signed URL for the matching precomputed floor-plan sidecar. */
  floorPlanUrl: string;
  timing: ClientBuilderPlanGltfTiming;
};
